import { z } from 'zod';
import { SuccessResponseSchema } from './common';

// User Schema (Base user schema)
export const UserSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  phone: z.string().regex(/^\+?[1-9]\d{1,14}$/),
  avatar: z.string().url().optional(),
  role: z.enum([
    'CUSTOMER',
    'RESTAURANT_OWNER',
    'DELIVERY_DRIVER',
    'MERCHANT',
    // admin-ish roles (backend AdminRole)
    'SUPER_ADMIN',
    'CONTENT_MANAGER',
    'USER_SUPPORT',
    'ANALYST',
    'MODERATOR',
    // legacy
    'ADMIN',
  ]),
  isEmailVerified: z.boolean(),
  isPhoneVerified: z.boolean(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

// Merchant Profile Schema
export const MerchantProfileSchema = z.object({
  userId: z.string(),
  merchantProfileId: z.string(),
  email: z.string().email(),
  fullName: z.string(),
  businessName: z.string(),
  businessAddress: z.string(),
  contactPersonName: z.string(),
  contactPhone: z.string().regex(/^\+?[1-9]\d{1,14}$/),
  contactEmail: z.string().email(),
  description: z.string().optional(),
  website: z.string().url().optional(),
  socialMediaLinks: z.object({
    instagram: z.string().url().optional(),
    facebook: z.string().url().optional(),
    twitter: z.string().url().optional(),
  }).optional(),
  profileCompletionPercentage: z.number().min(0).max(100),
  lastLoginAt: z.string().datetime(),
});

// Admin Profile Schema
export const AdminProfileSchema = z.object({
  userId: z.string(),
  adminProfileId: z.string(),
  email: z.string().email(),
  fullName: z.string(),
  accessLevel: z.enum([
    'SUPER_ADMIN',
    'CONTENT_MANAGER',
    'USER_SUPPORT',
    'ANALYST',
    'MODERATOR',
    // legacy
    'ADMIN',
  ]),
  department: z.string(),
  permissions: z.object({
    manage_users: z.boolean(),
    view_reports: z.boolean(),
    manage_restaurants: z.boolean().optional(),
    manage_orders: z.boolean().optional(),
    manage_payments: z.boolean().optional(),
  }),
  isActive: z.boolean(),
  lastLoginAt: z.string().datetime(),
});

// Authentication Tokens Schema
export const AuthTokensSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string(),
  expiresIn: z.number(),
  tokenType: z.literal('Bearer'),
  sessionId: z.string().uuid(),
});

// Login Request Schema
export const LoginRequestSchema = z.object({
  usernameOrEmail: z.string().email(),
  password: z.string().min(8),
});

// Register Request Schema (for regular users)
export const RegisterRequestSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8).regex(
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
    'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character'
  ),
  confirmPassword: z.string(),
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  phone: z.string().regex(/^\+?[1-9]\d{1,14}$/),
  role: z.enum(['CUSTOMER']).default('CUSTOMER'),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

// Admin Register Request Schema
export const AdminRegisterRequestSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8).regex(
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
    'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character'
  ),
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  phone: z.string().regex(/^\+?[1-9]\d{1,14}$/),
  department: z.string().default(''),
  accessLevel: z.enum(['ADMIN']).default('ADMIN'),
  permissions: z.object({
    manage_users: z.boolean().default(true),
    view_reports: z.boolean().default(true),
  }),
  notes: z.string().default(''),
});

// Merchant Register Request Schema
export const MerchantRegisterRequestSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8).regex(
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
    'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character'
  ),
  confirmPassword: z.string(),
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  businessName: z.string().min(1),
  businessAddress: z.string().min(1),
  contactPersonName: z.string().min(1),
  contactPhone: z.string().regex(/^\+?[1-9]\d{1,14}$/),
  contactEmail: z.string().email(),
  description: z.string().default(''),
  website: z.string().default(''),
  socialMediaLinks: z.object({
    instagram: z.string().default(''),
    facebook: z.string().default(''),
  }).default({ instagram: '', facebook: '' }),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

// Forgot Password Request Schema
export const ForgotPasswordRequestSchema = z.object({
  email: z.string().email(),
});

// Reset Password Request Schema
export const ResetPasswordRequestSchema = z.object({
  token: z.string(),
  password: z.string().min(8).regex(
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
    'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character'
  ),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

// Change Password Request Schema
export const ChangePasswordRequestSchema = z.object({
  currentPassword: z.string(),
  newPassword: z.string().min(8).regex(
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
    'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character'
  ),
  confirmNewPassword: z.string(),
}).refine((data) => data.newPassword === data.confirmNewPassword, {
  message: "New passwords don't match",
  path: ["confirmNewPassword"],
});

// Update Profile Request Schema
export const UpdateProfileRequestSchema = z.object({
  firstName: z.string().min(1).optional(),
  lastName: z.string().min(1).optional(),
  phone: z.string().regex(/^\+?[1-9]\d{1,14}$/).optional(),
  avatar: z.string().url().optional(),
});

// Verify Email Request Schema
export const VerifyEmailRequestSchema = z.object({
  token: z.string(),
});

// Verify Phone Request Schema
export const VerifyPhoneRequestSchema = z.object({
  code: z.string().length(6),
});

// OTP Verification Request Schema (for post-registration)
export const OTPVerificationRequestSchema = z.object({
  user_id: z.string().uuid(),
  verificationCode: z.string().length(6),
});

// Refresh Token Request Schema
export const RefreshTokenRequestSchema = z.object({
  refreshToken: z.string(),
});

// Response Types
// Merchant Login Response Schema (matches actual API response)
export const MerchantLoginResponseSchema = z.object({
  tokens: AuthTokensSchema,
  merchant: MerchantProfileSchema,
  message: z.string(),
});

// Admin Login - Actual API envelope and data schemas (based on provided response)
export const AdminAccountSchema = z.object({
  adminId: z.string().uuid(),
  username: z.string(),
  email: z.string().email(),
  fullName: z.string().nullable(),
  phone: z.string(),
  role: z
    .enum([
      'SUPER_ADMIN',
      'CONTENT_MANAGER',
      'USER_SUPPORT',
      'ANALYST',
      'MODERATOR',
      // legacy
      'ADMIN',
    ])
    .or(z.string()),
  isActive: z.boolean(),
  lastLogin: z.string(),
  createdAt: z.string(),
});

export const AdminLoginDataSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string(),
  tokenType: z.literal('Bearer').or(z.string()),
  expiresIn: z.number(),
  admin: AdminAccountSchema,
});

export const AdminLoginEnvelopeSchema = z.object({
  success: z.boolean(),
  message: z.string(),
  data: AdminLoginDataSchema,
  timestamp: z.string(),
});

// Backward-compatible schema retained for existing usages
export const AdminLoginResponseSchema = z.object({
  tokens: AuthTokensSchema,
  admin: AdminProfileSchema,
  message: z.string(),
});

// Legacy Login Response Schema (for backward compatibility)
export const LoginResponseSchema = SuccessResponseSchema(
  z.object({
    user: UserSchema,
    tokens: AuthTokensSchema,
  })
);

// Customer Register Response Schema (matches actual API response)
export const CustomerRegisterResponseSchema = z.object({
  tokens: AuthTokensSchema,
  user: UserSchema,
  message: z.string(),
});

// Admin Register Response Schema (matches actual API response)
export const AdminRegisterResponseSchema = z.object({
  tokens: AuthTokensSchema,
  admin: AdminProfileSchema,
  message: z.string(),
});

// Merchant Register Response Schema (matches actual API response)
export const MerchantRegisterResponseSchema = z.object({
  tokens: AuthTokensSchema,
  merchant: MerchantProfileSchema,
  message: z.string(),
});

// Legacy Register Response Schema (for backward compatibility)
export const RegisterResponseSchema = SuccessResponseSchema(
  z.object({
    user: UserSchema,
    tokens: AuthTokensSchema,
  })
);

export const ForgotPasswordResponseSchema = SuccessResponseSchema(
  z.object({
    message: z.string(),
  })
);

export const ResetPasswordResponseSchema = SuccessResponseSchema(
  z.object({
    message: z.string(),
  })
);

export const ChangePasswordResponseSchema = SuccessResponseSchema(
  z.object({
    message: z.string(),
  })
);

export const UpdateProfileResponseSchema = SuccessResponseSchema(UserSchema);

export const VerifyEmailResponseSchema = SuccessResponseSchema(
  z.object({
    message: z.string(),
  })
);

export const VerifyPhoneResponseSchema = SuccessResponseSchema(
  z.object({
    message: z.string(),
  })
);

// Email Verification Response Schema (for all user types)
export const EmailVerificationResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
  data: z.object({
    success: z.boolean(),
    message: z.string(),
    isEmailVerified: z.boolean(),
  }),
  timestamp: z.string(),
});

// OTP Verification Response Schema (legacy - for backward compatibility)
export const OTPVerificationResponseSchema = SuccessResponseSchema(
  z.object({
    user: UserSchema,
    tokens: AuthTokensSchema,
    message: z.string(),
  })
);

export const RefreshTokenResponseSchema = SuccessResponseSchema(AuthTokensSchema);

export const GetProfileResponseSchema = SuccessResponseSchema(UserSchema);

// TypeScript Types
export type User = z.infer<typeof UserSchema>;
export type MerchantProfile = z.infer<typeof MerchantProfileSchema>;
export type AdminProfile = z.infer<typeof AdminProfileSchema>;
export type AuthTokens = z.infer<typeof AuthTokensSchema>;
export type LoginRequest = z.infer<typeof LoginRequestSchema>;
export type RegisterRequest = z.infer<typeof RegisterRequestSchema>;
export type AdminRegisterRequest = z.infer<typeof AdminRegisterRequestSchema>;
export type MerchantRegisterRequest = z.infer<typeof MerchantRegisterRequestSchema>;
export type ForgotPasswordRequest = z.infer<typeof ForgotPasswordRequestSchema>;
export type ResetPasswordRequest = z.infer<typeof ResetPasswordRequestSchema>;
export type ChangePasswordRequest = z.infer<typeof ChangePasswordRequestSchema>;
export type UpdateProfileRequest = z.infer<typeof UpdateProfileRequestSchema>;
export type VerifyEmailRequest = z.infer<typeof VerifyEmailRequestSchema>;
export type VerifyPhoneRequest = z.infer<typeof VerifyPhoneRequestSchema>;
export type OTPVerificationRequest = z.infer<typeof OTPVerificationRequestSchema>;
export type RefreshTokenRequest = z.infer<typeof RefreshTokenRequestSchema>;

export type EmailVerificationResponse = z.infer<typeof EmailVerificationResponseSchema>;

export type MerchantLoginResponse = z.infer<typeof MerchantLoginResponseSchema>;
export type AdminLoginResponse = z.infer<typeof AdminLoginResponseSchema>;
export type AdminLoginData = z.infer<typeof AdminLoginDataSchema>;
export type AdminLoginEnvelope = z.infer<typeof AdminLoginEnvelopeSchema>;
export type LoginResponse = z.infer<typeof LoginResponseSchema>;

export type CustomerRegisterResponse = z.infer<typeof CustomerRegisterResponseSchema>;
export type AdminRegisterResponse = z.infer<typeof AdminRegisterResponseSchema>;
export type MerchantRegisterResponse = z.infer<typeof MerchantRegisterResponseSchema>;
export type RegisterResponse = z.infer<typeof RegisterResponseSchema>;
export type ForgotPasswordResponse = z.infer<typeof ForgotPasswordResponseSchema>;
export type ResetPasswordResponse = z.infer<typeof ResetPasswordResponseSchema>;
export type ChangePasswordResponse = z.infer<typeof ChangePasswordResponseSchema>;
export type UpdateProfileResponse = z.infer<typeof UpdateProfileResponseSchema>;
export type VerifyEmailResponse = z.infer<typeof VerifyEmailResponseSchema>;
export type VerifyPhoneResponse = z.infer<typeof VerifyPhoneResponseSchema>;
export type OTPVerificationResponse = z.infer<typeof OTPVerificationResponseSchema>;
export type RefreshTokenResponse = z.infer<typeof RefreshTokenResponseSchema>;
export type GetProfileResponse = z.infer<typeof GetProfileResponseSchema>;

// Union type for all possible user profiles
export type UserProfile = User | MerchantProfile | AdminProfile;

// Auth State Interface
export interface AuthState {
  user: UserProfile | null;
  tokens: AuthTokens | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}
